// illness schema + Bedrock tool definition (PLAN.md §6.1 Batch 4, D-15).
// A diagnosed condition with persistence. Pure data only — safe for both API and
// web SPA. ICD-10/SNOMED validation, regimen cross-links, and the activation/
// resolution cascades (§6.1.4) are Phase 2+ behaviors.

import { LATERALITY, type Laterality } from "./types.js";

export const ILLNESS_STATUSES = ["active", "resolved", "chronic", "in-remission", "suspected"] as const;
export type IllnessStatus = (typeof ILLNESS_STATUSES)[number];

export const ILLNESS_SEVERITIES = ["mild", "moderate", "severe"] as const;
export type IllnessSeverity = (typeof ILLNESS_SEVERITIES)[number];

/** Certainty of the diagnosis (clinician-style framing, not medical advice). */
export const DIAGNOSIS_TYPES = ["provisional", "confirmed", "differential", "rule-out"] as const;
export type DiagnosisType = (typeof DIAGNOSIS_TYPES)[number];

/** What the diagnosis was based on. */
export const DIAGNOSIS_BASES = ["exam", "imaging", "lab", "biopsy", "patient-reported"] as const;
export type DiagnosisBasis = (typeof DIAGNOSIS_BASES)[number];

/** Payload for an `illness` event (PLAN.md §6.1 Batch 4). */
export interface IllnessPayload {
  name: string; // R — free text Phase 1
  diagnosedOn: string; // R — ISO-8601 (date-only ok; auto-date per D-10)
  status: IllnessStatus; // R
  severity?: IllnessSeverity; // O
  onsetDate?: string; // O — when symptoms started (often earlier than diagnosis)
  diagnosingProvider?: string; // O
  icd10Code?: string; // O — free text Phase 1
  diagnosisType?: DiagnosisType; // O — provisional/confirmed/differential/rule-out
  bodySite?: string; // O — anatomical region free text
  laterality?: Laterality; // O — left/right/bilateral/midline/n-a
  basis?: DiagnosisBasis; // O — exam/imaging/lab/biopsy/patient-reported
  treatments?: string[]; // O — standing treatments
  complications?: string[]; // O
  resolvedAt?: string; // O — date status moved to resolved/in-remission
  // Acute-episode fields — meaningful when status=active and short-lived (Q5: one
  // illness type, status distinguishes acute episode vs. chronic condition).
  contagious?: boolean; // O
  missedDays?: number; // O — days of work/school missed
  symptoms?: string[]; // O — symptoms of this episode
  /** D-IE-1 (ILLNESS_EPISODE_TRACKING_PLAN.md): true for an acute, self-limiting
   *  or infectious bout (cold, flu, COVID, stomach bug, sinus/ear infection,
   *  bronchitis, strep, UTI). Drives the follow-up cards and the Illness episodes
   *  report. Absent/false = a chronic/standing condition, which is excluded from
   *  both. */
  episodic?: boolean; // O
  notes?: string; // O (full text when inline; see LargeNotesFields)
  notesPreview?: string; // O — populated by API when note overflows to S3
  notesRef?: string; // O — S3 key when note overflows; absent when inline
  // Step-5 soft-link (MEDICAL_HISTORY_EVENTS_PLAN.md §9, Option C):
  // optional, best-effort, no enforcement — dangling refs tolerated.
  sourceVisitId?: string; // O — eventId of the visit/hospitalization this dx came from
}

/**
 * Bedrock tool: record a diagnosed illness/condition. Needs the name and a
 * status. Omit diagnosedOn unless the user gave a date (server stamps today).
 */
export const RECORD_ILLNESS_TOOL = {
  name: "record_illness",
  description:
    "Record a diagnosed illness or medical condition (e.g. hypertension, type 2 diabetes, migraine, " +
    "asthma). Needs the condition name and its status (active/resolved/chronic/in-remission/suspected). " +
    "Capture severity, onset date, and diagnosing provider if the user gives them. Omit diagnosedOn " +
    "unless the user gave a date.",
  input_schema: {
    type: "object",
    properties: {
      name: { type: "string", description: "Condition name." },
      status: { type: "string", enum: [...ILLNESS_STATUSES] },
      diagnosedOn: { type: "string", description: "Calendar date YYYY-MM-DD in the patient's local timezone (no time, no offset). Omit if no date given." },
      severity: { type: "string", enum: [...ILLNESS_SEVERITIES] },
      onsetDate: { type: "string", description: "Calendar date YYYY-MM-DD (no time, no offset) symptoms started, if earlier than diagnosis." },
      diagnosingProvider: { type: "string" },
      icd10Code: { type: "string" },
      diagnosisType: { type: "string", enum: [...DIAGNOSIS_TYPES], description: "Certainty, if the user indicated it." },
      bodySite: { type: "string", description: "Anatomical site, if relevant (e.g. 'left knee')." },
      laterality: { type: "string", enum: [...LATERALITY] },
      basis: { type: "string", enum: [...DIAGNOSIS_BASES], description: "How it was diagnosed, if stated." },
      treatments: { type: "array", items: { type: "string" } },
      complications: { type: "array", items: { type: "string" } },
      resolvedAt: { type: "string", description: "Calendar date YYYY-MM-DD (no time, no offset) the condition resolved, if applicable." },
      contagious: { type: "boolean", description: "For acute episodes, if mentioned." },
      missedDays: { type: "number", description: "Days of work/school missed, for an acute episode." },
      symptoms: { type: "array", items: { type: "string" }, description: "Symptoms of this episode." },
      episodic: {
        type: "boolean",
        description:
          "Set true for an acute, self-limiting or infectious illness the person is going " +
          "through now or recently (cold, flu, COVID, stomach bug, food poisoning, sinus " +
          "infection, ear infection, bronchitis, strep throat, UTI, pink eye). Omit or set " +
          "false for a chronic or standing condition (hypertension, diabetes, asthma, a " +
          "migraine disorder).",
      },
      notes: { type: "string" },
    },
    required: ["name", "status"],
  },
} as const;

/**
 * Bedrock tool: record an update to an ONGOING acute illness episode the person
 * previously logged (ILLNESS_EPISODE_TRACKING_PLAN.md, D-IE-2). Writes a NEW
 * illness event; it does NOT create a new unrelated condition. The handler in
 * capture.ts resolves the target open episodic illness by illnessEpisodeKey
 * (or "most-recently-updated open" when name is omitted) and copies stable
 * descriptors forward, same as the POST /illness-follow-up endpoint.
 */
export const RECORD_ILLNESS_UPDATE_TOOL = {
  name: "record_illness_update",
  description:
    "Record an update to an ONGOING illness episode the person previously logged — " +
    "e.g. 'my cold is gone', 'still have the flu', 'feeling worse today', 'I was over " +
    "the stomach bug by Tuesday'. Writes a NEW illness event; it does NOT create a new " +
    "unrelated condition. For a brand-new illness the person has not mentioned before, " +
    "use record_illness instead. Provide `name` to target a specific ongoing illness; " +
    "omit it to target the most-recently-updated ongoing one. Set recovered=true when " +
    "the person says they are fully over it. NEVER guess whether they have recovered — " +
    "ask if it is unclear.",
  input_schema: {
    type: "object",
    properties: {
      name: { type: "string", description: "Illness name as previously reported, e.g. 'cold', 'flu'. Omit to target the most-recently-updated ongoing illness." },
      recovered: { type: "boolean", description: "True when the person says they are fully over it. Writes status=resolved + resolvedAt." },
      worse: { type: "boolean", description: "True when the person says it has gotten worse." },
      severity: { type: "string", enum: ["mild", "moderate", "severe"], description: "Current severity, if stated." },
      recoveredOn: { type: "string", description: "Calendar date YYYY-MM-DD they recovered, if earlier than today and stated. Only meaningful with recovered=true." },
    },
    required: [],
  },
} as const;
